/* eslint-disable */
import { useDispatch, useSelector } from 'react-redux';
import { X } from 'lucide-react';
import { modalActions } from '@/Actions';
import { Box, Modal } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { urls } from '@/Constants';

const modalStyles = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50rem',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
};

const POStatusBadge = ({ status }) => {
    const isDelivered = status === 'Delivered';
    return (
        <span className={`px-3 py-1 text-xs font-medium rounded-full ${isDelivered
            ? 'bg-green-100 text-green-600'
            : 'bg-blue-100 text-blue-600'
            }`}>
            {status}
        </span>
    );
};

const TransitPoDetailsModal = () => {
    const dispatch = useDispatch();
    const { show, data } = useSelector((state) => state.modal.transitPoDetailsModal);
    const storeList = useSelector((state) => state.form.STORES.data.LIST);
    const { orderImages, poOrders, vehicleNo } = data || {};

    const getStoreName = useCallback((storeId) => {
        const store = storeList.find((store) => store.id === storeId);
        return store?.name || 'Unknown Store';
    }, [storeList]);

    const totals = (poOrders ?? []).reduce((acc, po) => {
        acc.items += po.items?.length || 0;
        acc.amount += parseFloat(po.totalAmount?.replace(/[₹,]/g, '')) || 0;
        return acc;
    }, { items: 0, amount: 0 });

    const handleClose = () => {
        dispatch(modalActions.toggleTransitPoDetailsModal(false));
    };

    const calculatePOFinalCost = useCallback((po) => {
        if (!po?.items?.length) return 0;

        return po.items.reduce((total, item) => {
            const qty = item.qty || 0;
            const cost = item.productCost || 0;
            const tax = item.taxPercentage || 0;

            const itemTotal =
                qty * cost * (1 + tax / 100);

            return total + itemTotal;
        }, 0);
    }, []);

    const calculateWholePoFinalCost = useCallback((poOrders) => {
        if (!poOrders?.length) return 0;
        return poOrders.reduce((total, po) => {
            return total + calculatePOFinalCost(po);
        }, 0);
    }, []);

    return (
        <Modal
            open={show}
            aria-labelledby='po-details-modal'
            aria-describedby='po-details-modal'
            onClose={handleClose}
        >
            <Box sx={modalStyles}>
                <div className="w-full">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between p-4 border-b">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-800">PO Details</h2>
                            <p className="text-sm text-gray-500">
                                Truck: {vehicleNo}
                            </p>
                        </div>
                        <X
                            className="cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
                            size={24}
                            onClick={handleClose}
                        />
                    </div>

                    {/* Modal Body */}
                    <div className="p-4">
                        <div className="border rounded-lg overflow-hidden">
                            {/* Fixed Header */}
                            <table className="w-full table-fixed">
                                <thead>
                                    <tr className="bg-gray-50 border-b">
                                        <th className="w-[15%] px-4 py-3 text-left text-sm font-medium text-gray-600">PO Number</th>
                                        <th className="w-[35%] px-4 py-3 text-left text-sm font-medium text-gray-600">Deliver to</th>
                                        <th className="w-[12%] px-4 py-3 text-center text-sm font-medium text-gray-600">Items</th>
                                        <th className="w-[20%] px-4 py-3 text-center text-sm font-medium text-gray-600">Total Amount</th>
                                        <th className="w-[18%] px-4 py-3 text-center text-sm font-medium text-gray-600">Status</th>
                                    </tr>
                                </thead>
                            </table>

                            {/* Scrollable Body */}
                            <div className="overflow-y-auto max-h-[40vh]">
                                <table className="w-full table-fixed">
                                    <tbody className="divide-y divide-gray-200">
                                        {(poOrders ?? []).map((po, index) => (
                                            <tr key={index} className="hover:bg-gray-50">
                                                <td className="w-[15%] px-4 py-3 text-sm text-blue-600 cursor-pointer hover:underline"
                                                    onClick={() => window.open(`${urls.EXTENSION}${urls.PO_LIST}?poId=${po?.id}&paramNavSelected=${true}`, '_blank')}
                                                >
                                                    {po.id}
                                                </td>
                                                <td className="w-[35%] px-4 py-3 text-sm text-gray-700 truncate">{getStoreName(po?.storeId)}</td>
                                                <td className="w-[12%] px-4 py-3 text-sm text-gray-700 text-center">{po.items?.length}</td>
                                                <td className="w-[20%] px-4 py-3 text-sm text-gray-700 text-center">₹{calculatePOFinalCost(po).toFixed(2)}</td>
                                                <td className="w-[18%] px-4 py-3 text-center">
                                                    <POStatusBadge status={po.orderStatus} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Fixed Footer */}
                            <table className="w-full table-fixed border-t">
                                <tbody>
                                    <tr className="bg-gray-50 font-medium">
                                        <td className="w-[15%] px-4 py-3 text-sm text-gray-800">Totals</td>
                                        <td className="w-[35%] px-4 py-3 text-sm text-gray-800"></td>
                                        <td className="w-[12%] px-4 py-3 text-sm text-gray-800 text-center">{totals.items}</td>
                                        <td className="w-[20%] px-4 py-3 text-sm text-gray-800 text-center">₹{calculateWholePoFinalCost(poOrders).toFixed(2)}</td>
                                        <td className="w-[18%] px-4 py-3 text-sm text-gray-800"></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </Box>
        </Modal>
    );
};

export default TransitPoDetailsModal;